// Display-currency rates for the browser, relative to AUD (1 AUD = X).
//
// Fetched from our own /api/rates (src/app/api/rates/route.ts), which reads the
// public rate feed server-side and caches it, so a visitor's browser never
// sends its IP address to the feed. Most pages never ask: RootLayout hands
// every page the rates it rendered with (src/lib/display-rates-server.ts).
//
// NEVER A STATIC TABLE. A failed fetch used to resolve to a table typed in on
// 4 March 2026, and the theme context took it for live rates: buyers' prices
// drifted 7 to 28% (TRY 56%), and a seller's typed price was converted to AUD
// at it and STORED that way. A failure is now null and the caller keeps what
// it has. With no rate for the viewer's currency, the theme renders and
// accepts amounts in AUD (`displayRate` in src/context/theme-context.tsx) and
// keeps asking (watchConversionRates below) until one arrives.

import { usableRates, type DisplayRates } from './display-rates';

const RATES_URL = '/api/rates';

/** How long a fetched table is reused before the next call asks again. */
const CACHE_TTL_MS = 60 * 60 * 1000;

/**
 * A request still unanswered after this counts as failed, so a hung connection
 * cannot hold up the retries. /api/rates itself gives the feed 5 seconds.
 */
const REQUEST_TIMEOUT_MS = 15 * 1000;

/** Older browsers lack AbortSignal.timeout; there the request just has no deadline of its own, rather than failing every time. */
function requestDeadline(): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
    : undefined;
}

/**
 * A rates reader with its own cache. It answers the live table, or null when
 * there is none to be had: offline, an HTTP error, a body that is not a
 * success carrying usable rates, or a call outside a browser where the
 * relative URL does not resolve. It never rejects and never invents a rate.
 * Only a success is cached; calls made while a request is out share it.
 *
 * `fetch` is for tests. Without it the global is called per request, so a
 * stubbed global applies too.
 */
export function createRatesReader(options: {
  fetch?: typeof fetch;
  now?: () => number;
} = {}): () => Promise<DisplayRates | null> {
  const now = options.now ?? Date.now;
  let cached: { rates: DisplayRates; at: number } | null = null;
  let pending: Promise<DisplayRates | null> | null = null;

  const request = async (): Promise<DisplayRates | null> => {
    try {
      const init = { signal: requestDeadline() };
      const response = options.fetch ? await options.fetch(RATES_URL, init) : await fetch(RATES_URL, init);
      if (!response.ok) return null;
      const body = (await response.json()) as { result?: unknown; rates?: unknown } | null;
      if (body?.result !== 'success') return null;
      const rates = usableRates(body.rates);
      if (rates) cached = { rates, at: now() };
      return rates;
    } catch {
      return null;
    }
  };

  return () => {
    if (cached && now() - cached.at < CACHE_TTL_MS) return Promise.resolve(cached.rates);
    pending ??= request().finally(() => {
      pending = null;
    });
    return pending;
  };
}

/** The live table (1 AUD = X), or null when it cannot be had. See createRatesReader. */
export const getConversionRates = createRatesReader();

/**
 * How long to wait before asking again while a rate the page needs is still
 * missing: 15 seconds, 1 minute, 5 minutes, then every 15 minutes for as long
 * as it stays missing.
 */
export const RATE_RETRY_DELAYS_MS: readonly number[] = [15_000, 60_000, 5 * 60_000, 15 * 60_000];

/** The wait after `failures` attempts in a row (1 or more) came back without the rate. */
export function rateRetryDelayMs(failures: number): number {
  const step = Math.floor(failures);
  if (!(step >= 1)) return RATE_RETRY_DELAYS_MS[0];
  return RATE_RETRY_DELAYS_MS[Math.min(step, RATE_RETRY_DELAYS_MS.length) - 1];
}

/**
 * The least time since the last attempt before showing the tab again starts
 * another one, so switching tabs during an outage cannot ask faster than the
 * schedule's first step. Coming back online is not held to it.
 */
export const RATE_WAKE_MIN_GAP_MS = RATE_RETRY_DELAYS_MS[0];

/** An event source the watcher listens on: `window` for 'online', `document` for 'visibilitychange'. */
export interface WakeSource {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface RatesWatch {
  /** Whether a table holds every rate the page needs. Asking stops once one does. */
  covers: (rates: DisplayRates) => boolean;
  /** Handed every table that arrives, covering or not. */
  onRates: (rates: DisplayRates) => void;
  /** One attempt; null for a failure. Defaults to getConversionRates. */
  load?: () => Promise<DisplayRates | null>;
  setTimer?: (run: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  now?: () => number;
  /** Fires 'online'. Defaults to `window`; null listens for nothing. */
  connectivity?: WakeSource | null;
  /** Fires 'visibilitychange' and reports visibilityState. Defaults to `document`; null listens for nothing. */
  visibility?: (WakeSource & { readonly visibilityState?: string }) | null;
}

/**
 * Asks for the rates now, then on the RATE_RETRY_DELAYS_MS schedule until a
 * table covers what the page needs, and straight away when the browser comes
 * back online (the schedule then starts over) or the tab is shown again. One
 * attempt at a time. Returns a stop function; nothing that lands after it is
 * handed on.
 */
export function watchConversionRates(watch: RatesWatch): () => void {
  const load = watch.load ?? getConversionRates;
  const setTimer = watch.setTimer ?? ((run: () => void, ms: number) => setTimeout(run, ms));
  const clearTimer = watch.clearTimer ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const now = watch.now ?? Date.now;
  const connectivity = watch.connectivity !== undefined
    ? watch.connectivity
    : (typeof window === 'undefined' ? null : window);
  const visibility = watch.visibility !== undefined
    ? watch.visibility
    : (typeof document === 'undefined' ? null : document);

  let stopped = false;
  let running = false;
  let failures = 0;
  let timer: unknown = null;
  let lastAttemptAt = Number.NEGATIVE_INFINITY;

  const cancelTimer = () => {
    if (timer === null) return;
    clearTimer(timer);
    timer = null;
  };

  const attempt = async () => {
    if (stopped || running) return;
    cancelTimer();
    running = true;
    lastAttemptAt = now();
    let rates: DisplayRates | null = null;
    try {
      rates = await load();
    } catch {
      rates = null;
    }
    running = false;
    if (stopped) return;
    if (rates) watch.onRates(rates);
    if (stopped) return;
    if (rates && watch.covers(rates)) {
      stop();
      return;
    }
    failures += 1;
    timer = setTimer(() => {
      timer = null;
      void attempt();
    }, rateRetryDelayMs(failures));
  };

  const onOnline = () => {
    failures = 0;
    void attempt();
  };
  const onVisibilityChange = () => {
    if (visibility && visibility.visibilityState !== 'visible') return;
    if (now() - lastAttemptAt < RATE_WAKE_MIN_GAP_MS) return;
    void attempt();
  };

  function stop() {
    if (stopped) return;
    stopped = true;
    cancelTimer();
    connectivity?.removeEventListener('online', onOnline);
    visibility?.removeEventListener('visibilitychange', onVisibilityChange);
  }

  connectivity?.addEventListener('online', onOnline);
  visibility?.addEventListener('visibilitychange', onVisibilityChange);
  void attempt();
  return stop;
}
